import React, { useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { CallIntelConstant, CallIntelligenceV2, CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { sentry } from '@frontend/tracking';
import { StatsType } from './types';

type InViewLocationsChartStatsProps<TData> = {
  analysis: StatsType;
  dependencies?: StatsType[];
  drillDownOptions?: CallIntelTypes.DrillDownOptions;
  filters: CallIntelTypes.Filters;
  formatter: (args: {
    analysisData: CallIntelTypes.CallsViewByLocation[keyof CallIntelTypes.CallsViewByLocation];
    depsData: Record<StatsType, CallIntelTypes.CallsViewByLocation[keyof CallIntelTypes.CallsViewByLocation]>;
    locationsStats?: CallIntelTypes.CallsViewByLocation;
  }) => TData;
  render: (args: { data: TData; isLoading: boolean; ref: (node?: Element | null) => void }) => React.ReactNode;
  rootMargin?: string;
  threshold?: number | number[];
};

const chartToIncludeMap: Record<StatsType, CallIntelTypes.IncludesAnalysis> = {
  appointmentTypes: CallIntelTypes.IncludesAnalysis.CALL_APPOINTMENT_TYPE,
  categories: CallIntelTypes.IncludesAnalysis.CALL_CATEGORY,
  opportunities: CallIntelTypes.IncludesAnalysis.CALL_SCHEDULING_OUTCOME,
  schedulingStats: CallIntelTypes.IncludesAnalysis.CALL_SCHEDULING_OUTCOME,
  sentiments: CallIntelTypes.IncludesAnalysis.CALL_SENTIMENT,
  taskTypes: CallIntelTypes.IncludesAnalysis.FOLLOWUP_TASKS,
};

const includeToFieldMap: Partial<Record<StatsType, keyof CallIntelTypes.CallsViewByLocation>> = {
  appointmentTypes: 'appointmentTypesByLocation',
  categories: 'categoriesByLocation',
  opportunities: 'opportunitiesByLocation',
  schedulingStats: 'schedulingStatsByLocation',
  sentiments: 'sentimentsByLocation',
  taskTypes: 'taskTypesByLocation',
};

export function InViewLocationsChartStats<TData>({
  analysis,
  dependencies = [],
  drillDownOptions,
  filters,
  formatter,
  render,
  rootMargin = '200px',
  threshold = 0.01,
}: InViewLocationsChartStatsProps<TData>) {
  const { inView: isVisible, ref } = useInView({ triggerOnce: true, rootMargin, threshold });

  const requestPayload = useMemo(
    () => CallIntelUtils.generateChartsPayload({ drillDownOptions, filters }),
    [drillDownOptions, filters]
  );

  const analysisIncludes = useMemo(() => {
    const mainInclude = chartToIncludeMap[analysis];
    const dependencyIncludes = dependencies?.map((dep) => chartToIncludeMap[dep]) ?? [];
    const allIncludes = new Set<CallIntelTypes.IncludesAnalysis>([mainInclude, ...dependencyIncludes]);
    return Array.from(allIncludes);
  }, [analysis, dependencies]);

  const { data: locationsStats, isLoading } = CallIntelligenceV2.Queries.useGetCallsViewByLocationQuery({
    request: { ...requestPayload, includesAnalysis: analysisIncludes },
    options: {
      enabled: isVisible && !!filters.locations?.length,
      cacheTime: CallIntelConstant.DEFAULT_CACHE_TIME,
      staleTime: CallIntelConstant.DEFAULT_CACHE_TIME,
      onError: (error) => {
        sentry.warn({
          team: 'analytics',
          topic: 'call-intelligence',
          error: `Error fetching calls view by location data for ${analysis}: ${error}`,
          addContext: {
            name: 'filters',
            context: { payload: requestPayload, includesAnalysis: analysisIncludes },
          },
        });
      },
    },
  });

  // Store formatter in a ref to avoid triggering recomputation of formattedData
  // when the parent provides a new formatter reference with the same logic.
  const formatterRef = useRef(formatter);
  formatterRef.current = formatter;

  const formattedData = useMemo(() => {
    const primaryDataKey = includeToFieldMap[analysis];
    const analysisData = primaryDataKey ? locationsStats?.[primaryDataKey] : undefined;

    const depsData = (dependencies ?? []).reduce((accumulator, dependencyType) => {
      const dependencyFieldKey = includeToFieldMap[dependencyType];
      if (dependencyFieldKey && locationsStats) {
        accumulator[dependencyType] = locationsStats[dependencyFieldKey];
      }
      return accumulator;
    }, {} as Record<StatsType, CallIntelTypes.CallsViewByLocation[keyof CallIntelTypes.CallsViewByLocation]>);

    return formatterRef.current({ analysisData, depsData, locationsStats });
  }, [analysis, dependencies, locationsStats]);

  return <>{render({ data: formattedData, isLoading, ref })}</>;
}
